/**
 * StrategyForkService — Phase V (V4): "fork on fresh inputs".
 *
 * Creates a SUCCESSOR strategy from a predecessor: same source-data SELECTION
 * (entity ids recovered from the predecessor's frozen sourceData), hydrated FRESH,
 * generated through the server pipeline with D28 lineage recorded in the new
 * strategy's inputManifest. The predecessor is never modified (input-versioning.md §4).
 */
import type { Strategy } from '@segmently/schema-models/strategy';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';
import { taskConverter } from '@modules/firebase-core/converters/taskConverter';
import { SourceDataHydrator, type SourceData } from './SourceDataHydrator';
import { StrategyOrchestrator } from './StrategyOrchestrator';
import {
  normalizeImmutableForkHypothesis,
  resolveImmutableForkPredecessor,
} from './strategyForkLineage';

function idsOf(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item: any) => (item && typeof item === 'object' ? String(item.id ?? '') : ''))
    .filter(Boolean);
}

export interface ForkRequest {
  name: string;
  forkHypothesis: string;
  /** Pin a specific fresh snapshot; default = latest completed (server-side). */
  insightsAnalysisId?: string;
  generateScreens?: boolean;
  model?: string;
  maxTokens?: number;
}

export interface ForkResult {
  success: boolean;
  strategy?: Strategy;
  taskId?: string;
  error?: string;
}

export class StrategyForkService {
  constructor(private readonly projectId: string) {}

  /** The same selection the predecessor was generated from, recovered from its frozen sourceData. */
  selectionFrom(predecessor: { sourceData?: Record<string, unknown> }): SourceData {
    const src = (predecessor.sourceData ?? {}) as Record<string, unknown>;
    return {
      audienceIds: idsOf(src.audiences),
      taskIds: idsOf(src.tasks),
      studyCaseIds: idsOf(src.studycases ?? (src as any).studyCases),
      reviewIds: idsOf(src.reviews),
      ratingIds: idsOf(src.ratings),
      bigNumberIds: idsOf(src.bigNumbers),
    };
  }

  async forkOnFreshInputs(
    predecessor: Strategy & {
      id: string;
      sourceData?: Record<string, unknown>;
      status?: string;
      currentVersion?: string;
    },
    request: ForkRequest,
  ): Promise<ForkResult> {
    const immutablePredecessor = resolveImmutableForkPredecessor(predecessor);
    const forkHypothesis = normalizeImmutableForkHypothesis(request.forkHypothesis);
    const strategyService = new StrategyService(this.projectId);
    const hydrator = new SourceDataHydrator(
      this.projectId,
      new AudienceService(this.projectId),
      new TaskService(this.projectId, taskConverter),
      new StudyCaseService(this.projectId),
      new ReviewService(this.projectId),
      new RatingService(this.projectId),
      new BigNumberService(this.projectId),
    );
    const orchestrator = new StrategyOrchestrator(this.projectId, strategyService, hydrator);

    const result = await orchestrator.createWithAIServer(
      {
        name: request.name,
        description: (predecessor as any).description ?? '',
        strategyType: (predecessor as any).strategyType ?? 'IOS Onboarding',
        toneOfVoice: (predecessor as any).toneOfVoice ?? '',
        designDescription: (predecessor as any).designDescription ?? '',
        sourceDataMapping: (predecessor as any).sourceDataMapping ?? [],
        variables: (predecessor as any).variables ?? {},
        variableSetSourceId: (predecessor as any).variableSetSourceId,
      } as any,
      this.selectionFrom(predecessor),
      {
        /**
         * F-LIVE2-22: the fork dialog asks for neither a model nor a screens
         * choice, and the server's ladder used to read only the SUCCESSOR head,
         * which is fresh — so a fork landed on the provider default and always
         * generated screens.
         *
         * Ruling FW6-R3 (fix round 1): the SERVER owns both inheritances. This
         * client sends the request's own values and nothing else — an absent
         * model resolves through the ladder's `'predecessor'` rung (which also
         * inherits `lastGenerationMaxTokens` and records
         * `modelSource: 'predecessor'`), and an absent screens choice is defaulted
         * from the predecessor's `screenCount`. Reading the predecessor head here
         * too would fork the rule in two places and let a stale client value
         * outrank the server's.
         */
        generateScreens: request.generateScreens,
        model: request.model,
        maxTokens: request.maxTokens,
        insightsAnalysisId: request.insightsAnalysisId,
        predecessorStrategyId: immutablePredecessor.predecessorStrategyId,
        predecessorVersion: immutablePredecessor.predecessorVersion,
        forkHypothesis,
      },
    );
    return result;
  }
}
